import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Chip,
} from "@material-ui/core";
import { format, parseISO } from "date-fns";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const toLocalInputValue = (date) => {
  if (!date) return "";
  const d = typeof date === "string" ? parseISO(date) : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultRemindAt = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 30);
  return toLocalInputValue(d);
};

const AgentTaskModal = ({
  open,
  onClose,
  taskId = null,
  ticketId = null,
  contactId = null,
  contactName = "",
  initialRemindAt = null,
  onSaved,
}) => {
  const [initial, setInitial] = useState({
    title: "",
    body: "",
    remindAt: defaultRemindAt(),
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (!taskId) {
      const remindAt = initialRemindAt
        ? toLocalInputValue(initialRemindAt)
        : defaultRemindAt();
      setInitial({
        title: contactName ? `${i18n.t("tasks.followUp")}: ${contactName}` : "",
        body: "",
        remindAt,
      });
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agent-tasks");
        const found = (data || []).find((t) => t.id === taskId);
        if (found) {
          setInitial({
            title: found.title || "",
            body: found.body || "",
            remindAt: toLocalInputValue(found.remindAt),
          });
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, taskId, contactName, initialRemindAt]);

  const schema = Yup.object().shape({
    title: Yup.string().min(2).required(i18n.t("tasks.validation.title")),
    remindAt: Yup.string().required(i18n.t("tasks.validation.date")),
  });

  const handleSubmit = async (values, actions) => {
    try {
      const payload = {
        title: values.title,
        body: values.body,
        remindAt: new Date(values.remindAt).toISOString(),
        ticketId: ticketId || null,
        contactId: contactId || null,
      };

      if (taskId) {
        await api.put(`/agent-tasks/${taskId}`, payload);
        toast.success(i18n.t("tasks.updated"));
      } else {
        await api.post("/agent-tasks", payload);
        toast.success(i18n.t("tasks.created"));
      }

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {taskId ? i18n.t("tasks.modal.editTitle") : i18n.t("tasks.modal.createTitle")}
      </DialogTitle>
      <Formik
        initialValues={initial}
        enableReinitialize
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ touched, errors, isSubmitting, values }) => (
          <Form>
            <DialogContent dividers>
              {loading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <>
                  {(ticketId || contactName) && (
                    <Box mb={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                      {contactName && (
                        <Chip size="small" label={contactName} color="primary" variant="outlined" />
                      )}
                      {ticketId && (
                        <Chip size="small" label={`Ticket #${ticketId}`} variant="outlined" />
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {i18n.t("tasks.modal.hint")}
                  </Typography>
                  <Field
                    as={TextField}
                    name="title"
                    label={i18n.t("tasks.modal.title")}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <Field
                    as={TextField}
                    name="body"
                    label={i18n.t("tasks.modal.body")}
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    multiline
                    rows={4}
                  />
                  <Field
                    as={TextField}
                    name="remindAt"
                    label={i18n.t("tasks.modal.remindAt")}
                    type="datetime-local"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={touched.remindAt && Boolean(errors.remindAt)}
                    helperText={touched.remindAt && errors.remindAt}
                  />
                  {values.remindAt && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
                      {i18n.t("tasks.modal.remindPreview")}:{" "}
                      {format(new Date(values.remindAt), "dd/MM/yyyy HH:mm")}
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                {i18n.t("common.close")}
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting || loading}>
                {isSubmitting ? <CircularProgress size={22} /> : i18n.t("tasks.modal.save")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AgentTaskModal;
